import { apiInternals, send } from './client';

/**
 * The call-centre FAQ (M-12, Task 4) — `GET/POST /knowledge-base`,
 * `GET/PATCH/DELETE /knowledge-base/{article}`. Every shape here is
 * verified field-for-field against `App\Http\Resources\Knowledge\
 * KnowledgeBaseArticleResource` and the FormRequests in
 * `app/Http/Requests/Knowledge/` in the API repository, not inferred from
 * the task brief's prose — the identical discipline every sibling module in
 * this directory states.
 *
 * ## K-6: content is stored as text and rendered as text. THAT DEFENCE LIVES
 * IN `ArticleList.tsx`, NOT HERE
 *
 * `question`/`answer` travel through this module as plain strings, exactly
 * as the wire carries them (`KnowledgeBaseArticleResource`'s own docblock:
 * "published exactly as stored... no escaping, no stripping"). This file
 * does nothing to either field — no sanitising, no encoding — because
 * there is nothing here that renders them. The only place a stored
 * `<script>` payload could become a DOM element is the render site, and
 * that site renders both fields as a React text child, never
 * `dangerouslySetInnerHTML` (`ArticleList.tsx`'s own docblock, spec K-6).
 *
 * ## `list` is built on `request`, never `send` (the Global Constraint every
 * cursor-paginated module in this codebase repeats)
 *
 * `GET /knowledge-base` is cursor-paginated exactly like `GET /justifications`
 * — `{ data, meta: { next_cursor, per_page, total } }`. `send()` resolves to
 * `request()`'s `.data` alone and throws `meta` away, which would leave the
 * "next page" control with nothing to read.
 *
 * ## `published` is a boolean, `1`/`0` on the wire, never `true`/`false`
 * (Global Constraint)
 *
 * Laravel's `boolean` validation rule accepts `true`/`false`/`1`/`0`/`'1'`/
 * `'0'` — the STRING `'true'` (`String(true)`) is rejected. `queryString()`
 * below carries the identical fix `requests.ts`/`justifications.ts`/
 * `escalations.ts` each document at length.
 *
 * ## `can.manage` — what THIS CALLER may do to THIS row (the C-1 fix, restated
 * for this module)
 *
 * `KnowledgeBaseArticleResource` publishes `can.manage`, computed
 * server-side from `KnowledgeBaseArticlePolicy::update()`/`::delete()` for
 * the authenticated caller — never re-derived here from `permissions` alone.
 * `src/lib/permissions/knowledge.ts`'s own `canManageKnowledge` gates only
 * the SCREEN-LEVEL affordances (the filter, the create button) that have no
 * row to read an ability from; a row's own edit/delete controls read
 * `article.can.manage` directly.
 *
 * ## There is no publish endpoint of its own (spec §2)
 *
 * Publishing is one field on `PATCH`'s body (`is_published`), not a
 * transition route — `ArticlePublisher` is entirely server-side machinery
 * this module has no need to call separately.
 */

/** The four fixed values `StoreArticleRequest`/`UpdateArticleRequest` allow (spec K-3). */
export type ArticleCategory = 'sensor' | 'replacement' | 'defect' | 'other';

/** `locale` — a plain tag on the entry, not a translation pair (spec K-4). Defaults to `en`. */
export type ArticleLocale = 'en' | 'ar';

/**
 * What THIS CALLER may do to THIS row — `KnowledgeBaseArticleResource`'s
 * `can` key. `manage` is exactly `KnowledgeBaseArticlePolicy::update()`/
 * `::delete()` (one permission gates both, so one boolean answers for edit,
 * publish/unpublish AND delete alike — that resource's own docblock).
 */
export interface KnowledgeArticleAbilities {
  manage: boolean;
}

/**
 * One FAQ entry, exactly as every one of the five routes renders it —
 * the SAME shape everywhere (`KnowledgeBaseArticleResource`'s own class
 * docblock, "one shape everywhere").
 */
export interface KnowledgeArticle {
  uuid: string;
  question: string;
  answer: string;
  category: ArticleCategory;
  locale: ArticleLocale;
  is_published: boolean;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  can: KnowledgeArticleAbilities;
}

/**
 * Every filter `GET /knowledge-base` accepts (`ArticleIndexRequest::rules()`,
 * spec K-3, K-4, K-5, §2). All optional.
 *
 * `published` IS SENT ONLY WHEN THE CALLER HOLDS `knowledge.manage` — see
 * `ArticleList.tsx`'s own docblock. The server accepts it from any caller
 * and silently ignores it for one without the permission (spec §2), but
 * this client never relies on that: a non-manage caller's query never
 * carries the key at all (task brief Step 4's own requirement).
 */
export interface KnowledgeArticleQuery {
  /** Matched case-insensitively against BOTH `question` and `answer` (spec K-5) — never `question` alone. */
  q?: string;
  category?: ArticleCategory;
  locale?: ArticleLocale;
  published?: boolean;
  cursor?: string;
  per_page?: number;
}

/** One page of the FAQ — cursor-paginated, exactly `JustificationPage`'s shape. */
export interface KnowledgeArticlePage {
  data: readonly KnowledgeArticle[];
  meta: {
    next_cursor: string | null;
    per_page: number;
    /** How many articles match the selected filters, not how many are on this page. */
    total: number;
  };
}

/**
 * `POST /knowledge-base`'s body (`StoreArticleRequest`). NO `is_published`
 * here, deliberately (spec K-2) — a new entry is always a draft; the server
 * would drop the field via mass assignment even if it were sent, but this
 * type does not offer the field at all, so a caller cannot be misled into
 * thinking it does anything here.
 */
export interface StoreArticleInput {
  question: string;
  answer: string;
  category: ArticleCategory;
  /** Defaults to `en` server-side when omitted (spec K-4). */
  locale?: ArticleLocale;
}

/**
 * `PATCH /knowledge-base/{article}`'s body (`UpdateArticleRequest`). Every
 * field optional — a caller may edit any subset. `is_published` is the ONE
 * field `StoreArticleInput` does not have (spec K-2, K-8): setting it `true`
 * for the first time stamps `published_at` and writes the
 * `knowledge.published` notification fan-out; setting it `true` again is a
 * no-op. Omitting the key entirely leaves publication state untouched.
 */
export interface UpdateArticleInput {
  question?: string;
  answer?: string;
  category?: ArticleCategory;
  locale?: ArticleLocale;
  is_published?: boolean;
}

/**
 * `?key=value&...`, or `''` when every value is absent. `undefined` is
 * dropped rather than becoming the literal string `'undefined'` in the URL —
 * the identical helper every sibling module in this directory redeclares
 * locally for the same reason (none is exported).
 */
function queryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, string | number | boolean | undefined][]) {
    if (value === undefined) continue;

    // A BOOLEAN GOES OVER THE WIRE AS `1`/`0`, NEVER `true`/`false` — see this
    // module's own docblock for why `published=true` would 422.
    search.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
  }
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

const { request } = apiInternals;

export const knowledgeApi = {
  /**
   * DELIBERATELY NOT `send<KnowledgeArticlePage>()` — see this module's own
   * docblock for why that would compile and still lose `meta.next_cursor`.
   */
  list: async (query: KnowledgeArticleQuery = {}): Promise<KnowledgeArticlePage> => {
    const { data, meta } = await request<readonly KnowledgeArticle[]>(
      `/knowledge-base${queryString(query)}`,
    );
    return { data, meta: meta as KnowledgeArticlePage['meta'] };
  },

  /** `404` for both an unpublished entry and a nonexistent uuid, for a caller without `knowledge.manage` (spec §2). */
  get: (uuid: string) => send<KnowledgeArticle>(`/knowledge-base/${uuid}`),

  /** Always creates a draft (spec K-2) — see `StoreArticleInput`'s own docblock. */
  create: (input: StoreArticleInput) =>
    send<KnowledgeArticle>('/knowledge-base', { method: 'POST', body: input }),

  /** Content edit and/or publish/unpublish, in one call (spec K-2, K-8). */
  update: (uuid: string, input: UpdateArticleInput) =>
    send<KnowledgeArticle>(`/knowledge-base/${uuid}`, { method: 'PATCH', body: input }),

  /** Soft-deletes; `204` (spec K-1, K-9 — never the legacy's hard `DELETE FROM`). */
  remove: (uuid: string) => send<void>(`/knowledge-base/${uuid}`, { method: 'DELETE' }),
};
